/**
 * Contract Validation Schema - Sử dụng Yup để định nghĩa validation rules
 * Đồng bộ với backend Laravel/NestJS
 */

import * as yup from "yup";

/**
 * Schema validation cho từng field của contract
 * @type {Object}
 */
export const contractSchema = {
  customer_name: yup
    .string()
    .required("Họ và tên không được để trống")
    .min(2, "Họ và tên phải có ít nhất 2 ký tự"),

  customer_email: yup
    .string()
    .required("Email không được để trống")
    .email("Email không đúng định dạng"),

  customer_phone: yup
    .string()
    .required("Số điện thoại không được để trống")
    .matches(/^0[3-9][0-9]{8}$/, "Số điện thoại không đúng định dạng"),

  customer_birthday: yup
    .string()
    .required("Ngày sinh không được để trống")
    .test(
      "valid-date",
      "Ngày sinh không đúng định dạng (DD-MM-YYYY hoặc D/M/YY)",
      function (value) {
        if (!value) return false;

        const regex1 = /^(\d{2})-(\d{2})-(\d{4})$/; // DD-MM-YYYY
        const regex2 = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/; // D/M/YY hoặc DD/MM/YYYY

        let day, month, year;

        if (regex1.test(value)) {
          [, day, month, year] = value.match(regex1);
        } else if (regex2.test(value)) {
          [, day, month, year] = value.match(regex2);
          if (year.length === 2) {
            year = parseInt(year) < 50 ? "20" + year : "19" + year;
          }
        } else {
          return false;
        }

        const date = new Date(year, month - 1, day);

        return (
          date.getFullYear() == year &&
          date.getMonth() == month - 1 &&
          date.getDate() == day
        );
      }
    ),

  customer_address: yup.string().nullable(),

  customer_cccd: yup.string().nullable(),

  // Required numeric fields
  center_id: yup
    .number()
    .typeError("Trung tâm phải là số hợp lệ")
    .required("Trung tâm không được để trống")
    .min(1, "Trung tâm phải là số lớn hơn 0"),

  branch_id: yup
    .number()
    .typeError("Chi nhánh phải là số hợp lệ")
    .required("Chi nhánh không được để trống")
    .min(1, "Chi nhánh phải là số lớn hơn 0"),

  tuition_payment_id: yup
    .number()
    .typeError("Hình thức đóng học phí phải là số hợp lệ")
    .required("Hình thức đóng học phí không được để trống")
    .min(1, "Hình thức đóng học phí phải là số lớn hơn 0"),

  tvv_id: yup
    .number()
    .typeError("Mã tư vấn phải là số hợp lệ")
    .required("Mã tư vấn không được để trống")
    .min(1, "Mã tư vấn phải là số lớn hơn 0"),

  user_intro_id: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Mã giới thiệu phải là số hợp lệ")
    .min(1, "Mã giới thiệu phải là số lớn hơn 0"),

  // Required array fields
  course_ids: yup
    .array()
    .of(
      yup
        .number()
        .typeError("Khóa học phải là số hợp lệ")
        .min(1, "ID khóa học phải là số lớn hơn 0")
    )
    .min(1, "Khóa học không được để trống")
    .required("Khóa học không được để trống"),

  // Optional array fields
  promotion_ids: yup
    .array()
    .of(
      yup
        .number()
        .typeError("Mã khuyến mại phải là số hợp lệ")
        .min(1, "ID khuyến mại phải là số lớn hơn 0")
    )
    .nullable()
    .default([]),
};

/**
 * Validate một field với schema tương ứng
 * @param {String} field - Tên field
 * @param {*} value - Giá trị cần validate
 * @returns {Promise<Object>} Kết quả validation
 */
export async function validateField(field, value) {
  if (!contractSchema[field]) {
    return { isValid: true };
  }

  try {
    await contractSchema[field].validate(value);
    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      message: error.message,
    };
  }
}

/**
 * Validate toàn bộ object với schema
 * @param {Object} data - Object cần validate
 * @returns {Promise<Object>} Kết quả validation
 */
export async function validateObject(data) {
  const errors = {};

  for (const field in contractSchema) {
    const result = await validateField(field, data[field]);
    if (!result.isValid) {
      errors[field] = result.message;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
